import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PublicPageProps } from '@/types'

import { cms } from '@/lib/cms'
import { useI18n } from '@/i18n'
import { Seo } from '@/components/generals/seo'
import { PageIntro, SiteLayout } from '@/components/site/site-layout'
import { loadPublicProps } from '@/lib/server/ssr'

const ImprintPage: FC<PublicPageProps> = (base): ReactNode => {
    const { t } = useI18n()
    const { settings } = base

    return (
        <SiteLayout {...base}>
            <Seo title={t.meta.imprintTitle} />

            <PageIntro title={t.legal.imprintTitle} />

            <section id='legal-notice' className='mx-auto max-w-3xl px-4 py-14 sm:px-6 md:py-20'>
                <div className='grid gap-10 sm:grid-cols-2'>
                    <div>
                        <h2 className='font-sans text-sm font-bold tracking-[0.15em] text-gold uppercase'>{t.legal.imprintProvider}</h2>
                        <p className='mt-3 text-lg font-semibold text-navy' {...cms('site_settings', settings.id, 'company_name')}>{settings.companyName}</p>
                        {settings.address && <p className='mt-1 text-foreground/80' {...cms('site_settings', settings.id, 'address')}>{settings.address}</p>}
                        {settings.ownerName && (
                            <p className='mt-4 text-foreground/80'>
                                <span>{t.legal.imprintRepresented}: </span>
                                <span {...cms('site_settings', settings.id, 'owner_name')}>{settings.ownerName}</span>
                            </p>
                        )}
                    </div>

                    <div>
                        <h2 className='font-sans text-sm font-bold tracking-[0.15em] text-gold uppercase'>{t.legal.imprintContact}</h2>
                        <dl className='mt-3 flex flex-col gap-2 text-foreground/80'>
                            {settings.contactEmail && (
                                <div>
                                    <dt className='inline font-semibold text-navy'>{t.legal.imprintEmail}: </dt>
                                    <dd className='inline'>
                                        <a href={`mailto:${settings.contactEmail}`} className='hover:underline'>
                                            <span {...cms('site_settings', settings.id, 'contact_email')}>{settings.contactEmail}</span>
                                        </a>
                                    </dd>
                                </div>
                            )}
                            {settings.phone && (
                                <div>
                                    <dt className='inline font-semibold text-navy'>{t.legal.imprintPhone}: </dt>
                                    <dd className='inline' dir='ltr' {...cms('site_settings', settings.id, 'phone')}>{settings.phone}</dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>

                {settings.ownerName && (
                    <div className='mt-10'>
                        <h2 className='font-sans text-sm font-bold tracking-[0.15em] text-gold uppercase'>{t.legal.imprintResponsible}</h2>
                        <p className='mt-3 text-foreground/80'>
                            <span {...cms('site_settings', settings.id, 'owner_name')}>{settings.ownerName}</span>
                        </p>
                    </div>
                )}

                <div className='mt-12 rounded-3xl bg-sand p-6'>
                    <h2 className='font-sans text-base font-bold text-navy'>{t.legal.imprintDisclaimerTitle}</h2>
                    <p className='mt-2 text-sm leading-relaxed text-foreground/75'>{t.legal.imprintDisclaimer}</p>
                </div>
            </section>
        </SiteLayout>
    )
}

export const getServerSideProps: GetServerSideProps<PublicPageProps> = async (ctx) => ({ props: await loadPublicProps(ctx) })

export default ImprintPage
